import { calculatePay, normalizePeriods, roundMoney, type PeriodMap } from './calculatePay';
import { PAYROLL_CONFIG } from './payroll.config';
import type { CourseGradeEntry, PeriodAnomaly } from 'src/types/payroll.types';

export type PayrollMatchStatus =
  | 'matched'
  | 'unmatched'
  | 'duplicate_in_file'
  | 'invalid_grade'
  | 'already_paid'
  | 'name_mismatch_warning'
  | 'zero_gross'
  | 'inactive'
  | 'posted'
  | 'post_failed';

export interface ParsedPayrollRow {
  rowNumber: number;
  externalStudentId: string;
  firstName: string;
  lastName: string;
  gradeLevel: string;
  datePulled: string;
  periods: PeriodMap;
  courses?: CourseGradeEntry[];
  anomalies?: PeriodAnomaly[];
  gross: number;
  taxRate: number;
  taxWithheld: number;
  net: number;
  periodSum: number;
  /** Validation flags before student matching */
  parseErrors: string[];
  invalidPeriodKeys: number[];
  warnings?: string[];
}

export interface ParsePayrollCsvResult {
  rows: ParsedPayrollRow[];
  fileName: string;
  headerIssues: string[];
}

const HEADER_ALIASES: Record<string, string> = {
  student_id: 'student_id',
  studentid: 'student_id',
  'student id': 'student_id',
  'student id #': 'student_id',
  'lms id': 'student_id',
  id: 'student_id',
  first_name: 'first_name',
  firstname: 'first_name',
  'first name': 'first_name',
  first: 'first_name',
  last_name: 'last_name',
  lastname: 'last_name',
  'last name': 'last_name',
  last: 'last_name',
  grade_level: 'grade_level',
  gradelevel: 'grade_level',
  grade: 'grade_level',
  'grade level': 'grade_level',
  email: 'email',
  e_mail: 'email',
  'e mail': 'email',
  'e-mail': 'email',
  student_email: 'email',
  studentemail: 'email',
  'student email': 'email',
  'user email': 'email',
  user_email: 'email',
  useremail: 'email',
  date_pulled: 'date_pulled',
  datepulled: 'date_pulled',
  'date pulled': 'date_pulled',
  date: 'date_pulled',
  period_1_pct: 'period_1',
  period_2_pct: 'period_2',
  period_3_pct: 'period_3',
  period_4_pct: 'period_4',
  period_5_pct: 'period_5',
  period_6_pct: 'period_6',
  period_7_pct: 'period_7',
  period_8_pct: 'period_8',
  'period 1 %': 'period_1',
  'period 2 %': 'period_2',
  'period 3 %': 'period_3',
  'period 4 %': 'period_4',
  'period 5 %': 'period_5',
  'period 6 %': 'period_6',
  'period 7 %': 'period_7',
  'period 8 %': 'period_8',
  'period 1': 'period_1',
  'period 2': 'period_2',
  'period 3': 'period_3',
  'period 4': 'period_4',
  'period 5': 'period_5',
  'period 6': 'period_6',
  'period 7': 'period_7',
  'period 8': 'period_8',
  period1: 'period_1',
  period2: 'period_2',
  period3: 'period_3',
  period4: 'period_4',
  period5: 'period_5',
  period6: 'period_6',
  period7: 'period_7',
  period8: 'period_8',
  p1: 'period_1',
  p2: 'period_2',
  p3: 'period_3',
  p4: 'period_4',
  p5: 'period_5',
  p6: 'period_6',
  p7: 'period_7',
  p8: 'period_8',
};

function normalizeHeader(raw: string): string {
  return raw
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

function mapHeader(raw: string): string | null {
  const key = normalizeHeader(raw);
  if (HEADER_ALIASES[key]) return HEADER_ALIASES[key];
  // period_N_pct style after space normalize → "period 1 pct"
  const periodMatch = key.match(/^period\s*([1-8])\s*(%|pct|percent)?$/);
  if (periodMatch) return `period_${periodMatch[1]}`;
  // collapsed aliases without spaces
  const collapsed = key.replace(/\s/g, '');
  if (HEADER_ALIASES[collapsed]) return HEADER_ALIASES[collapsed];
  return null;
}

/** Minimal RFC4180-ish CSV line split supporting quoted fields. */
export function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
}

export function parseCsvText(text: string): string[][] {
  const normalized = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const lines = normalized.split('\n').filter((line) => line.trim().length > 0);
  return lines.map(splitCsvLine);
}

function parsePeriodValue(raw: string | undefined): { value: number; error?: string } {
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return { value: 0 };
  }
  const cleaned = String(raw).trim().replace(/%/g, '');
  // letter grades not allowed for numerical score
  if (/^[A-Fa-f][+-]?$/.test(cleaned) || /[a-zA-Z]/.test(cleaned)) {
    return { value: 0, error: `Non-numeric grade "${raw}"` };
  }
  const n = Number(cleaned);
  if (!Number.isFinite(n)) {
    return { value: 0, error: `Invalid number "${raw}"` };
  }
  if (n < 0) {
    return { value: 0, error: `Period cannot be negative (${raw})` };
  }
  // Enforce 100 max cap ($100 max pay per period / extra credit clamped to 100)
  return { value: Math.min(100, n) };
}

export function applyAnomalyResolution(
  currentPeriods: PeriodMap,
  anomalies: PeriodAnomaly[] | undefined,
  periodName: string,
  resolution: 'keep_both' | 'keep_first' | 'keep_second' | 'average',
  taxRate: number = PAYROLL_CONFIG.taxRate
): {
  periods: PeriodMap;
  anomalies: PeriodAnomaly[];
  gross: number;
  taxWithheld: number;
  net: number;
  periodSum: number;
} {
  const updatedPeriods: PeriodMap = { ...currentPeriods };

  // Remove any existing keys for this period (e.g. '3', '3A', '3B', etc.)
  Object.keys(updatedPeriods).forEach((k) => {
    if (k === periodName || (k.startsWith(periodName) && !/^\d+$/.test(k))) {
      delete updatedPeriods[k];
    }
  });

  const updatedAnomalies: PeriodAnomaly[] = (anomalies || []).map((a) => {
    if (a.periodName !== periodName) return a;
    return { ...a, resolution };
  });

  const targetAnomaly = (anomalies || []).find((a) => a.periodName === periodName);
  if (targetAnomaly && targetAnomaly.courses.length > 0) {
    const c1 = targetAnomaly.courses[0];
    const c2 = targetAnomaly.courses[1] || targetAnomaly.courses[0];

    if (resolution === 'keep_both') {
      const letters = ['A', 'B', 'C', 'D', 'E'];
      targetAnomaly.courses.forEach((c, idx) => {
        const letter = letters[idx] || String(idx + 1);
        updatedPeriods[`${periodName}${letter}`] = c.progressPercent;
      });
    } else if (resolution === 'keep_first') {
      updatedPeriods[periodName] = c1.progressPercent;
    } else if (resolution === 'keep_second') {
      updatedPeriods[periodName] = c2.progressPercent;
    } else if (resolution === 'average') {
      const sum = targetAnomaly.courses.reduce((s, c) => s + c.progressPercent, 0);
      const avg = sum / targetAnomaly.courses.length;
      updatedPeriods[periodName] = roundMoney(Math.min(100, avg));
    }
  }

  const pay = calculatePay(updatedPeriods, taxRate);

  return {
    periods: pay.periods,
    anomalies: updatedAnomalies,
    gross: pay.gross,
    taxWithheld: pay.taxWithheld,
    net: pay.net,
    periodSum: pay.periodSum,
  };
}

export function parseSisPayrollCsv(
  table: string[][],
  fileName = 'payroll.csv',
  taxRate: number = PAYROLL_CONFIG.taxRate
): ParsePayrollCsvResult {
  const rawHeaders = table[0];
  const headerMap: Record<string, number> = {};
  rawHeaders.forEach((h, idx) => {
    headerMap[h.trim().toLowerCase()] = idx;
  });

  const getCol = (possibleNames: string[]): number | undefined => {
    for (const name of possibleNames) {
      if (headerMap[name] !== undefined) return headerMap[name];
    }
    return undefined;
  };

  const studentNumIdx = getCol(['student.studentnumber', 'studentnumber', 'student_number', 'student_id']);
  const firstNameIdx = getCol(['student.firstname', 'firstname', 'first_name']);
  const lastNameIdx = getCol(['student.lastname', 'lastname', 'last_name']);
  const gradeIdx = getCol(['student.grade', 'grade', 'grade_level']);
  const activeTodayIdx = getCol(['student.activetoday', 'activetoday', 'active']);
  const courseNameIdx = getCol(['grading.coursename', 'coursename', 'course_name', 'course']);
  const periodNameIdx = getCol(['grading.periodname', 'periodname', 'period_name', 'period']);
  const progressScoreIdx = getCol(['grading.progressscore', 'progressscore', 'letter_grade', 'grade_score']);
  const progressPercentIdx = getCol(['grading.progresspercent', 'progresspercent', 'percent', 'percentage']);

  const headerIssues: string[] = [];
  if (studentNumIdx === undefined) headerIssues.push('Missing required column: student.studentNumber');
  if (firstNameIdx === undefined) headerIssues.push('Missing required column: student.firstName');
  if (lastNameIdx === undefined) headerIssues.push('Missing required column: student.lastName');
  if (periodNameIdx === undefined) headerIssues.push('Missing required column: grading.periodName');
  if (progressPercentIdx === undefined) headerIssues.push('Missing required column: grading.progressPercent');

  if (headerIssues.length > 0) {
    return { rows: [], fileName, headerIssues };
  }

  interface RawStudentRecord {
    studentNumber: string;
    firstName: string;
    lastName: string;
    grade: string;
    activeToday: string;
    courses: Array<{
      courseName: string;
      periodName: string;
      progressScore: string;
      progressPercent: string;
    }>;
  }

  const studentOrder: string[] = [];
  const studentsMap = new Map<string, RawStudentRecord>();

  for (let r = 1; r < table.length; r += 1) {
    const row = table[r];
    const sid = (row[studentNumIdx!] ?? '').trim();
    if (!sid) continue;

    if (!studentsMap.has(sid)) {
      studentOrder.push(sid);
      studentsMap.set(sid, {
        studentNumber: sid,
        firstName: (row[firstNameIdx!] ?? '').trim(),
        lastName: (row[lastNameIdx!] ?? '').trim(),
        grade: gradeIdx !== undefined ? (row[gradeIdx] ?? '').trim() : '',
        activeToday: activeTodayIdx !== undefined ? (row[activeTodayIdx] ?? '').trim() : '1',
        courses: [],
      });
    }

    const record = studentsMap.get(sid)!;
    record.courses.push({
      courseName: courseNameIdx !== undefined ? (row[courseNameIdx] ?? '').trim() : '',
      periodName: (row[periodNameIdx!] ?? '').trim(),
      progressScore: progressScoreIdx !== undefined ? (row[progressScoreIdx] ?? '').trim() : '',
      progressPercent: (row[progressPercentIdx!] ?? '').trim(),
    });
  }

  const rows: ParsedPayrollRow[] = [];

  studentOrder.forEach((sid, idx) => {
    const s = studentsMap.get(sid)!;
    const parseErrors: string[] = [];
    const warnings: string[] = [];
    const invalidPeriodKeys: number[] = [];
    const anomalies: PeriodAnomaly[] = [];
    const courseGradeEntries: CourseGradeEntry[] = [];
    const periodsRaw: Record<string | number, number> = {};

    const cleanGrade = s.grade.replace(/\D/g, '') || s.grade;

    if (s.activeToday === '0') {
      warnings.push('Student marked inactive in school export (student.activeToday = 0)');
    }

    // Group student's courses by periodName
    const byPeriod = new Map<string, Array<{ courseName: string; score: string; pct: number }>>();

    s.courses.forEach((c) => {
      const pName = c.periodName || '1';
      const { value, error } = parsePeriodValue(c.progressPercent);
      if (error) {
        parseErrors.push(`Period ${pName}: ${error}`);
        const pNum = parseInt(pName, 10);
        if (!isNaN(pNum)) invalidPeriodKeys.push(pNum);
      }
      if (!byPeriod.has(pName)) {
        byPeriod.set(pName, []);
      }
      byPeriod.get(pName)!.push({
        courseName: c.courseName || `Period ${pName}`,
        score: c.progressScore,
        pct: value,
      });
    });

    // Sort periods naturally (e.g. 1, 2, 3...)
    const sortedPeriodNames = Array.from(byPeriod.keys()).sort((a, b) => {
      const numA = parseInt(a, 10);
      const numB = parseInt(b, 10);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });

    sortedPeriodNames.forEach((periodName) => {
      const courseList = byPeriod.get(periodName)!;

      if (courseList.length === 1) {
        const c = courseList[0];
        periodsRaw[periodName] = c.pct;
        courseGradeEntries.push({
          periodKey: periodName,
          periodName,
          courseName: c.courseName,
          progressScore: c.score,
          progressPercent: c.pct,
        });
      } else {
        // Multiple courses in the same period!
        const firstScore = courseList[0].pct;
        const allIdentical = courseList.every((c) => c.pct === firstScore);

        if (allIdentical) {
          // Identical scores: Scholar 4868 rule (deduplicate to single entry)
          periodsRaw[periodName] = firstScore;
          courseGradeEntries.push({
            periodKey: periodName,
            periodName,
            courseName: courseList.map((c) => c.courseName).filter(Boolean).join(' / ') || `Period ${periodName}`,
            progressScore: courseList[0].score,
            progressPercent: firstScore,
          });
          warnings.push(
            `Period ${periodName} duplicate courses with identical scores (${firstScore}%) deduplicated: ${courseList.map((c) => c.courseName).join(', ')}`
          );
          anomalies.push({
            periodName,
            courses: courseList.map((c) => ({
              courseName: c.courseName,
              progressScore: c.score,
              progressPercent: c.pct,
            })),
            isIdentical: true,
            resolution: 'keep_first',
          });
        } else {
          // Different scores: 3A / 3B split (default recommended: pay both)
          const letters = ['A', 'B', 'C', 'D', 'E'];
          courseList.forEach((c, cIdx) => {
            const letter = letters[cIdx] || String(cIdx + 1);
            const periodKey = `${periodName}${letter}`;
            periodsRaw[periodKey] = c.pct;
            courseGradeEntries.push({
              periodKey,
              periodName,
              courseName: c.courseName,
              progressScore: c.score,
              progressPercent: c.pct,
            });
          });

          const coursesDesc = courseList.map((c) => `${c.courseName} (${c.pct}%)`).join(', ');
          warnings.push(`Period ${periodName} anomaly: 2 courses found (${coursesDesc}) — defaulted to 3A/3B`);
          anomalies.push({
            periodName,
            courses: courseList.map((c) => ({
              courseName: c.courseName,
              progressScore: c.score,
              progressPercent: c.pct,
            })),
            isIdentical: false,
            resolution: 'keep_both',
          });
        }
      }
    });

    const periods = normalizePeriods(periodsRaw);
    const pay = calculatePay(periods, taxRate);

    rows.push({
      rowNumber: idx + 1,
      externalStudentId: s.studentNumber,
      firstName: s.firstName,
      lastName: s.lastName,
      gradeLevel: cleanGrade,
      datePulled: '',
      periods: pay.periods,
      courses: courseGradeEntries,
      anomalies,
      gross: pay.gross,
      taxRate: pay.taxRate,
      taxWithheld: pay.taxWithheld,
      net: pay.net,
      periodSum: pay.periodSum,
      parseErrors,
      invalidPeriodKeys,
      warnings,
    });
  });

  return { rows, fileName, headerIssues };
}

export function parsePayrollCsv(
  text: string,
  fileName = 'payroll.csv',
  taxRate: number = PAYROLL_CONFIG.taxRate
): ParsePayrollCsvResult {
  const table = parseCsvText(text);
  if (table.length === 0) {
    return { rows: [], fileName, headerIssues: ['CSV is empty'] };
  }

  const rawHeaders = table[0];
  const isSis = rawHeaders.some((h) => {
    const lower = h.trim().toLowerCase();
    return (
      lower.includes('student.studentnumber') ||
      lower.includes('grading.periodname') ||
      lower.includes('studentnumber') ||
      lower.includes('student.lastname')
    );
  });

  if (isSis) {
    return parseSisPayrollCsv(table, fileName, taxRate);
  }

  // Fallback to wide format parser
  const mappedHeaders = rawHeaders.map(mapHeader);
  const headerIssues: string[] = [];

  const hasStudentId = mappedHeaders.includes('student_id');
  const hasFirst = mappedHeaders.includes('first_name');
  const hasLast = mappedHeaders.includes('last_name');
  if (!hasStudentId) headerIssues.push('Missing required column: Student ID');
  if (!hasFirst) headerIssues.push('Missing required column: First Name');
  if (!hasLast) headerIssues.push('Missing required column: Last Name');

  const colIndex: Record<string, number> = {};
  mappedHeaders.forEach((h, i) => {
    if (h) colIndex[h] = i;
  });

  const rows: ParsedPayrollRow[] = [];

  for (let r = 1; r < table.length; r += 1) {
    const cells = table[r];
    const get = (key: string) => {
      const idx = colIndex[key];
      if (idx === undefined) return '';
      return (cells[idx] ?? '').trim();
    };

    const externalStudentId = get('student_id');
    const firstName = get('first_name');
    const lastName = get('last_name');
    const gradeLevel = get('grade_level');
    const datePulled = get('date_pulled');

    const parseErrors: string[] = [];
    const invalidPeriodKeys: number[] = [];
    const periodsRaw: Record<number, number> = {};

    if (!externalStudentId) parseErrors.push('Missing Student ID');
    if (!firstName) parseErrors.push('Missing First Name');
    if (!lastName) parseErrors.push('Missing Last Name');

    for (let p = 1; p <= PAYROLL_CONFIG.maxPeriods; p += 1) {
      const { value, error } = parsePeriodValue(get(`period_${p}`));
      periodsRaw[p] = value;
      if (error) {
        parseErrors.push(`Period ${p}: ${error}`);
        invalidPeriodKeys.push(p);
      }
    }

    const periods = normalizePeriods(periodsRaw);
    const pay = calculatePay(periods, taxRate);

    rows.push({
      rowNumber: r + 1,
      externalStudentId,
      firstName,
      lastName,
      gradeLevel,
      datePulled,
      periods: pay.periods,
      gross: pay.gross,
      taxRate: pay.taxRate,
      taxWithheld: pay.taxWithheld,
      net: pay.net,
      periodSum: pay.periodSum,
      parseErrors,
      invalidPeriodKeys,
    });
  }

  return { rows, fileName, headerIssues };
}

export interface ParsedRosterRow {
  rowNumber: number;
  externalStudentId: string;
  firstName: string;
  lastName: string;
  gradeLevel: string;
  email?: string;
  parseErrors: string[];
}

export function parseRosterCsv(text: string, fileName = 'roster.csv'): {
  rows: ParsedRosterRow[];
  fileName: string;
  headerIssues: string[];
} {
  const table = parseCsvText(text);
  if (table.length === 0) {
    return { rows: [], fileName, headerIssues: ['CSV is empty'] };
  }

  const rawHeaders = table[0];
  const isSis = rawHeaders.some((h) => {
    const lower = h.trim().toLowerCase();
    return (
      lower.includes('student.studentnumber') ||
      lower.includes('grading.periodname') ||
      lower.includes('studentnumber') ||
      lower.includes('student.lastname')
    );
  });

  if (isSis) {
    const headerMap: Record<string, number> = {};
    rawHeaders.forEach((h, idx) => {
      headerMap[h.trim().toLowerCase()] = idx;
    });

    const getCol = (possibleNames: string[]): number | undefined => {
      for (const name of possibleNames) {
        if (headerMap[name] !== undefined) return headerMap[name];
      }
      return undefined;
    };

    const sidIdx = getCol(['student.studentnumber', 'studentnumber', 'student_number', 'student_id']);
    const firstIdx = getCol(['student.firstname', 'firstname', 'first_name']);
    const lastIdx = getCol(['student.lastname', 'lastname', 'last_name']);
    const gradeIdx = getCol(['student.grade', 'grade', 'grade_level']);
    const emailIdx = getCol(['email', 'student.email', 'email_address', 'email address']);

    const headerIssues: string[] = [];
    if (sidIdx === undefined) headerIssues.push('Missing required column: student.studentNumber');
    if (firstIdx === undefined) headerIssues.push('Missing required column: student.firstName');
    if (lastIdx === undefined) headerIssues.push('Missing required column: student.lastName');

    if (headerIssues.length > 0) {
      return { rows: [], fileName, headerIssues };
    }

    const seenIds = new Set<string>();
    const rows: ParsedRosterRow[] = [];

    for (let r = 1; r < table.length; r += 1) {
      const cells = table[r];
      const sid = (cells[sidIdx!] ?? '').trim();
      if (!sid || seenIds.has(sid)) continue;
      seenIds.add(sid);

      const firstName = (cells[firstIdx!] ?? '').trim();
      const lastName = (cells[lastIdx!] ?? '').trim();
      const rawGrade = gradeIdx !== undefined ? (cells[gradeIdx] ?? '').trim() : '';
      const cleanGrade = rawGrade.replace(/\D/g, '') || rawGrade;
      const email = emailIdx !== undefined ? (cells[emailIdx] ?? '').trim() : undefined;

      const parseErrors: string[] = [];
      if (!sid) parseErrors.push('Missing Student ID');
      if (!firstName) parseErrors.push('Missing First Name');
      if (!lastName) parseErrors.push('Missing Last Name');

      rows.push({
        rowNumber: rows.length + 1,
        externalStudentId: sid,
        firstName,
        lastName,
        gradeLevel: cleanGrade,
        email: email || undefined,
        parseErrors,
      });
    }

    return { rows, fileName, headerIssues };
  }

  const mappedHeaders = rawHeaders.map(mapHeader);
  const headerIssues: string[] = [];
  if (!mappedHeaders.includes('student_id')) headerIssues.push('Missing required column: Student ID');
  if (!mappedHeaders.includes('first_name')) headerIssues.push('Missing required column: First Name');
  if (!mappedHeaders.includes('last_name')) headerIssues.push('Missing required column: Last Name');

  const colIndex: Record<string, number> = {};
  mappedHeaders.forEach((h, i) => {
    if (h) colIndex[h] = i;
  });

  const rows: ParsedRosterRow[] = [];
  for (let r = 1; r < table.length; r += 1) {
    const cells = table[r];
    const get = (key: string) => {
      const idx = colIndex[key];
      if (idx === undefined) return '';
      return (cells[idx] ?? '').trim();
    };
    const externalStudentId = get('student_id');
    const firstName = get('first_name');
    const lastName = get('last_name');
    const gradeLevel = get('grade_level');
    const email = get('email');
    const parseErrors: string[] = [];
    if (!externalStudentId) parseErrors.push('Missing Student ID');
    if (!firstName) parseErrors.push('Missing First Name');
    if (!lastName) parseErrors.push('Missing Last Name');
    rows.push({
      rowNumber: r + 1,
      externalStudentId,
      firstName,
      lastName,
      gradeLevel,
      email: email || undefined,
      parseErrors,
    });
  }

  return { rows, fileName, headerIssues };
}

export function downloadTextFile(filename: string, content: string, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const PAYROLL_CSV_TEMPLATE = `student.lastName,student.firstName,student.grade,student.studentNumber,student.activeToday,grading.progressScore,grading.progressPercent,grading.courseName,grading.periodName
Lee,Jordan,9th,300001001,1,A-,90,Biology (Hon),1
Lee,Jordan,9th,300001001,1,B,85,World History,2
Lee,Jordan,9th,300001001,1,B+,88,Math 9,3
Lee,Jordan,9th,300001001,1,A,92,English 9,4
Lee,Jordan,9th,300001001,1,B-,80,Visual Arts I,5
Lee,Jordan,9th,300001001,1,A,95,Physical Education I,6
Lee,Jordan,9th,300001001,1,B+,87,Spanish I,7
Lee,Jordan,9th,300001001,1,A-,91,Robotics I,8
`;

export const ROSTER_CSV_TEMPLATE = `student.lastName,student.firstName,student.grade,student.studentNumber,student.activeToday
Lee,Jordan,9th,300001001,1
Rivera,Sam,10th,300001002,1
`;
